import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getClientPositionRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface ClientPositionRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

interface ClientPositionItem {
  label: string;
  value: string | undefined;
  showOther?: boolean;
  otherValue?: string | undefined;
}

export default function ClientPositionRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  onRefresh,
  isViewOnly,
}: ClientPositionRowProps) {
  const { position, inWater, inWaterOther, notes, hasAnswers, hasNotes } = useMemo(
    () =>
      observation
        ? getClientPositionRowValues(observation)
        : {
            position: undefined,
            inWater: undefined,
            inWaterOther: undefined,
            notes: undefined,
            hasAnswers: false,
            hasNotes: false,
          },
    [observation],
  );

  const positionItems = useMemo<ClientPositionItem[]>(
    () => [
      { label: "Client's Position", value: position },
      {
        label: "Client in Water",
        value: inWater,
        showOther: inWater === "Other",
        otherValue: inWaterOther,
      },
    ],
    [position, inWater, inWaterOther],
  );

  if (!observation) return null;
  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Position:</TextWrapper>
            <List fz="sm" withPadding>
              {positionItems.map(
                (item, index) =>
                  (item.value || (item.showOther && item.otherValue)) && (
                    <List.Item key={`${item.label}-${item.value || item.otherValue}`}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}:
                        </TextWrapper>
                        <TextWrapper size="sm">
                          {item.showOther && item.otherValue
                            ? `${item.value} - ${item.otherValue}`
                            : item.value}
                        </TextWrapper>
                      </Group>
                    </List.Item>
                  ),
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Position Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={ClientPositionRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
